import { CONTROLLED_VOCABULARY_ASSIGNMENT, isControlledVocabularyAssignment, isSemanticModelClassProfile, isSemanticModelRelationshipProfile, SEMANTIC_MODEL_CLASS_PROFILE, SEMANTIC_MODEL_RELATIONSHIP_PROFILE, } from "../concepts/index.js";
import { isCreateSemanticModelClassProfile, isModifySemanticModelClassProfile, isCreateSemanticModelRelationshipProfile, isModifySemanticModelRelationshipProfile, isCreateControlledVocabularyAssignment, isRemoveControlledVocabularyAssignment, isModifyControlledVocabularyAssignment, isModifySemanticModelRelationshipEndProfile } from "./operations.js";
class DefaultSemanticModelProfileOperationExecutor {
    entityReader;
    entityWriter;
    constructor(entityReader, entityWriter) {
        this.entityReader = entityReader;
        this.entityWriter = entityWriter;
    }
    executeOperation(operation) {
        if (isCreateSemanticModelClassProfile(operation)) {
            return executeCreateSemanticModelClassProfile(this.entityWriter, operation);
        }
        if (isModifySemanticModelClassProfile(operation)) {
            return executeModifySemanticModelClassProfile(this.entityReader, this.entityWriter, operation);
        }
        if (isCreateSemanticModelRelationshipProfile(operation)) {
            return executeCreateSemanticModelRelationshipProfile(this.entityWriter, operation);
        }
        if (isModifySemanticModelRelationshipProfile(operation)) {
            return executeModifySemanticModelRelationshipProfile(this.entityReader, this.entityWriter, operation);
        }
        if (isCreateControlledVocabularyAssignment(operation)) {
            return executeCreateControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isRemoveControlledVocabularyAssignment(operation)) {
            return executeRemoveControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isModifyControlledVocabularyAssignment(operation)) {
            return executeModifyControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isModifySemanticModelRelationshipEndProfile(operation)) {
            return executeModifySemanticModelRelationshipEndProfile(this.entityReader, this.entityWriter, operation);
        }
        return null;
    }
}
function executeCreateSemanticModelClassProfile(entityWriter, { entity }) {
    const identifier = entity.id;
    const newEntity = {
        ...defaultClassProfile(),
        ...entity,
        id: identifier,
        type: [SEMANTIC_MODEL_CLASS_PROFILE],
    };
    entityWriter.change({ [identifier]: newEntity }, []);
    return {
        success: true,
        created: [identifier],
    };
}
function defaultClassProfile() {
    return {
        iri: null,
        name: null,
        nameFromProfiled: null,
        description: null,
        descriptionFromProfiled: null,
        usageNote: null,
        usageNoteFromProfiled: null,
        profiling: [],
        externalDocumentationUrl: null,
        tags: [],
        controlledVocabularies: [],
    };
}
function executeModifySemanticModelClassProfile(entityReader, entityWriter, { identifier, entity }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelClassProfile(previous)) {
        console.error("Previous values is not class profile, action to update the profile is ignored.", { previous, next: entity });
        return {
            success: false,
            created: [],
        };
    }
    ;
    const updatedEntity = {
        // The actual entity can be more specialized than SemanticModelClassProfile
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_CLASS_PROFILE],
        description: entity.description ?? previous.description,
        descriptionFromProfiled: mergeFromProfiled(entity.descriptionFromProfiled, previous.descriptionFromProfiled),
        name: entity.name ?? previous.name,
        nameFromProfiled: mergeFromProfiled(entity.nameFromProfiled, previous.nameFromProfiled),
        iri: entity.iri ?? previous.iri,
        usageNote: entity.usageNote ?? previous.usageNote,
        usageNoteFromProfiled: mergeFromProfiled(entity.usageNoteFromProfiled, previous.usageNoteFromProfiled),
        profiling: entity.profiling ?? previous.profiling,
        externalDocumentationUrl: mergeFromProfiled(entity.externalDocumentationUrl, previous.externalDocumentationUrl),
        tags: mergeFromProfiled(entity.tags, previous.tags),
        order: mergeFromProfiled(entity.order, previous.order) ?? null,
        controlledVocabularies: mergeFromProfiled(entity.controlledVocabularies, previous.controlledVocabularies),
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
function mergeFromProfiled(next, previous) {
    // We need to store null, if next is null.
    if (next === undefined) {
        return previous;
    }
    return next;
}
function executeCreateSemanticModelRelationshipProfile(entityWriter, { entity }) {
    const identifier = entity.id;
    const newEntity = {
        ...entity,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends: (entity.ends ?? []).map(item => ({
            ...defaultRelationshipEndProfile(),
            ...item,
        })),
    };
    entityWriter.change({ [identifier]: newEntity }, []);
    return {
        success: true,
        created: [identifier],
    };
}
function defaultRelationshipEndProfile() {
    return {
        name: null,
        nameFromProfiled: null,
        description: null,
        descriptionFromProfiled: null,
        iri: null,
        cardinality: null,
        usageNote: null,
        usageNoteFromProfiled: null,
        profiling: [],
        externalDocumentationUrl: null,
        tags: [],
        order: null,
    };
}
function executeModifySemanticModelRelationshipProfile(entityReader, entityWriter, { identifier, entity }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelRelationshipProfile(previous)) {
        console.error("Previous values is not relationship profile, action to update the profile is ignored.", { previous, next: entity });
        return {
            success: false,
            created: [],
        };
    }
    ;
    // When no ends are give, use the one from previous state
    const ends = entity.ends === undefined ? previous.ends :
        // Else we merge old to new, otherwise we would not be able to delete.
        entity.ends.map((value, index) => ({
            ...(previous.ends[index] ?? {}),
            ...value
        }));
    const updatedEntity = {
        // The actual entity can be more specialized than SemanticModelRelationshipProfile
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends,
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
function executeCreateControlledVocabularyAssignment(entityReader, entityWriter, { entity }) {
    const classProfile = entityReader.entity(entity.classProfile);
    if (classProfile === null || !isSemanticModelClassProfile(classProfile)) {
        console.error("Target is not a class profile, create controlled vocabulary assignment is ignored.", { entity });
        return { success: false, created: [] };
    }
    const existingOwn = classProfile.controlledVocabularies ?? [];
    // A class profile can only have one of its own assignments per vocabulary
    const existingSiblings = existingOwn
        .map(id => entityReader.entity(id))
        .filter(isControlledVocabularyAssignment);
    if (existingSiblings.some(a => a.vocabulary === entity.vocabulary)) {
        console.error("This class profile already has an own controlled vocabulary assignment for this vocabulary, create controlled vocabulary assignment is ignored.", { entity });
        return { success: false, created: [] };
    }
    const identifier = entity.id;
    const newEntity = {
        replaces: null,
        iri: null,
        ...entity,
        id: identifier,
        type: [CONTROLLED_VOCABULARY_ASSIGNMENT],
    };
    const updatedClassProfile = {
        ...classProfile,
        controlledVocabularies: [...existingOwn, identifier],
    };
    entityWriter.change({ [identifier]: newEntity, [classProfile.id]: updatedClassProfile }, []);
    return { success: true, created: [identifier] };
}
function executeRemoveControlledVocabularyAssignment(entityReader, entityWriter, { identifier }) {
    const assignment = entityReader.entity(identifier);
    if (assignment === null || !isControlledVocabularyAssignment(assignment)) {
        console.error("Target is not a controlled vocabulary assignment, remove controlled vocabulary assignment is ignored.", { identifier });
        return { success: false, created: [] };
    }
    const updated = {};
    const classProfile = entityReader.entity(assignment.classProfile);
    if (classProfile !== null && isSemanticModelClassProfile(classProfile)) {
        const updatedClassProfile = {
            ...classProfile,
            controlledVocabularies: (classProfile.controlledVocabularies ?? []).filter(id => id !== identifier),
        };
        updated[classProfile.id] = updatedClassProfile;
    }
    // No scan for other assignments whose `replaces.target` points at this
    // one - a dangling `replaces` is read as "no override" 
    entityWriter.change(updated, [identifier]);
    return { success: true, created: [] };
}
function executeModifyControlledVocabularyAssignment(entityReader, entityWriter, { identifier, changes }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isControlledVocabularyAssignment(previous)) {
        console.error("Target is not a controlled vocabulary assignment, modify controlled vocabulary assignment is ignored.", { identifier });
        return { success: false, created: [] };
    }
    const updatedEntity = { ...previous, ...changes };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return { success: true, created: [] };
}
function executeModifySemanticModelRelationshipEndProfile(entityReader, entityWriter, { identifier, endIndex, end }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelRelationshipProfile(previous)) {
        console.error("Previous value is not relationship profile, action to update the end profile is ignored.", { previous, next: end });
        return {
            success: false,
            created: [],
        };
    }
    const previousEnd = previous.ends[endIndex];
    if (previousEnd === undefined) {
        console.error("End index out of bounds, action to update the end profile is ignored.", { endIndex, ends: previous.ends });
        return {
            success: false,
            created: [],
        };
    }
    const ends = [...previous.ends];
    ends[endIndex] = { ...previousEnd, ...end };
    const updatedEntity = {
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends,
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
export function createDefaultSemanticModelProfileOperationExecutor(entityReader, entityWriter) {
    return new DefaultSemanticModelProfileOperationExecutor(entityReader, entityWriter);
}
//# sourceMappingURL=operations-executor.js.map